export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const VISITOR_KEY = 'eauget_visitor_id';
const SESSION_KEY = 'eauget_analytics_session';
const INDEX_KEY = 'eauget_visitor_session_index';
const BOOKED_FLAG = 'eauget_analytics_session_booked';
const IDLE_MS = 30 * 60 * 1000;

function uuid(): string {
  // crypto.randomUUID is available in all target browsers; fall back defensively.
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c?.randomUUID ? c.randomUUID() : `v-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

export function getVisitorId(store: KeyValueStore): string {
  let id = store.getItem(VISITOR_KEY);
  if (!id) { id = uuid(); store.setItem(VISITOR_KEY, id); }
  return id;
}

interface StoredSession { id: string; startedAt: number; lastActivityAt: number }

export interface ResolvedSession {
  sessionId: string;
  startedAt: number;
  visitorSessionIndex: number;
  isNew: boolean;
}

/** Mark the current session as booked so the NEXT resolve starts a fresh one. */
export function markBooked(store: KeyValueStore): void {
  store.setItem(BOOKED_FLAG, '1');
}

export function resolveSession(store: KeyValueStore, now: number): ResolvedSession {
  const booked = store.getItem(BOOKED_FLAG) === '1';
  const raw = store.getItem(SESSION_KEY);
  let prev: StoredSession | null = null;
  if (raw) { try { prev = JSON.parse(raw) as StoredSession; } catch { prev = null; } }

  const expired = !prev || booked || now - prev.lastActivityAt > IDLE_MS;

  if (expired) {
    const index = (parseInt(store.getItem(INDEX_KEY) ?? '0', 10) || 0) + 1;
    const session: StoredSession = { id: uuid(), startedAt: now, lastActivityAt: now };
    store.setItem(SESSION_KEY, JSON.stringify(session));
    store.setItem(INDEX_KEY, String(index));
    if (booked) store.setItem(BOOKED_FLAG, '0');
    return { sessionId: session.id, startedAt: now, visitorSessionIndex: index, isNew: true };
  }

  const session: StoredSession = { ...prev!, lastActivityAt: now };
  store.setItem(SESSION_KEY, JSON.stringify(session));
  const index = parseInt(store.getItem(INDEX_KEY) ?? '1', 10) || 1;
  return { sessionId: session.id, startedAt: session.startedAt, visitorSessionIndex: index, isNew: false };
}
